<template>
  <div>
    <div class="columns">
      <div class="column">
        <b-notification :closable="false">
          <span v-html="$t('compCarbonAdminDataAdmin.info-1')" />
        </b-notification>
        <b-notification :closable="false">
          <div class="columns">
            <div class="column">
              <b-field :label="$t('compCarbonAdminDataAdmin.label-15')">
                <b-dropdown
                  v-model="yearFilter"
                  multiple
                  aria-role="list"
                  @input="upateGHGIs"
                >
                  <template #trigger>
                    <b-button icon-right="caret-down">
                      {{ yearFilter.length }}
                      {{ $t('compCarbonAdminDataAdmin.label-16') }}
                    </b-button>
                  </template>
                  <b-dropdown-item
                    v-for="(year, index) in allYears"
                    :key="index"
                    :value="year"
                    aria-role="listitem"
                  >
                    <span>
                      {{ year }}
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
              </b-field>
            </div>
            <div class="column">
              <b-field :label="$t('compCarbonAdminDataAdmin.label-13')">
                <b-switch v-model="submittedFilter" />
              </b-field>
            </div>
          </div>
        </b-notification>

        <b-notification :closable="false">
          <b-dropdown
            :disabled="filteredGHGI.length === 0"
            v-model="cModule"
            aria-role="list"
            expanded
          >
            <template #trigger>
              <b-button
                :label="exportLabel"
                expanded
                icon-left="download"
                icon-right="caret-down"
              />
            </template>
            <b-dropdown-item
              v-for="(module, index) in dropdownModules"
              :key="index"
              :value="module"
              aria-role="listitem"
            >
              <div class="media">
                <b-icon class="media-left" :icon="module.icon"></b-icon>
                <div class="media-content">
                  <h3>{{ module.text }}</h3>
                </div>
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </b-notification>
      </div>
      <div class="column">
        <b-carousel
          :autoplay="false"
          indicator-style="is-lines"
          :indicator-inside="true"
        >
          <b-carousel-item>
            <ghgis-boxplot
              :ghgis="filteredGHGI"
              :modules="modules"
              :colors="colors"
            />
          </b-carousel-item>
          <b-carousel-item>
            <ghgis-timelines :ghgis="filteredGHGI" />
          </b-carousel-item>
        </b-carousel>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <b-notification :closable="false">
          <div class="level-item has-text-centered">
            <div>
              <p>
                {{ $t('compCarbonAdminDataAdmin.label-1') }}
              </p>
              <div class="title is-size-4">
                <p>
                  <span>{{ filteredGHGI.length }}</span>
                </p>
              </div>
            </div>
          </div>
        </b-notification>
      </div>
      <div class="column">
        <b-notification :closable="false">
          <div class="level-item has-text-centered">
            <div>
              <p>
                {{ $t('compCarbonAdminDataAdmin.label-10') }}
              </p>
              <div class="title is-size-4">
                <p>
                  <span>{{ nStructure }}</span>
                </p>
              </div>
            </div>
          </div>
        </b-notification>
      </div>
      <div class="column">
        <b-notification :closable="false">
          <div class="level-item has-text-centered">
            <div>
              <p>
                {{ $t('compCarbonAdminDataAdmin.label-12') }}
              </p>
              <div class="title is-size-4">
                <p>
                  <span
                    >{{ averageIntensity | splitNumberTons }}
                    {{ $t('t.co2e') }}</span
                  >
                </p>
              </div>
            </div>
          </div>
        </b-notification>
      </div>
    </div>

    <br />
    <b-table
      :data="filteredGHGI"
      :striped="true"
      :hoverable="true"
      :sort-icon="'angle-up'"
      paginated
      per-page="50"
      :default-sort="['year', 'workforce']"
      detailed
      detail-transition="fade"
    >
      <b-table-column
        field="year"
        sortable
        :label="$t('year')"
        numeric
        v-slot="props"
      >
        {{ props.row.year }}
      </b-table-column>
      <b-table-column
        field="entity.name"
        searchable
        sortable
        :label="$t('compCarbonAdminDataAdmin.label-3')"
        centered
        v-slot="props"
      >
        <b-tooltip multilined :label="props.row.entity.name" type="is-black">
          {{ props.row.entity.name | truncate(15) }}
        </b-tooltip>
      </b-table-column>
      <b-table-column
        field="entity.referent.email"
        searchable
        sortable
        :label="$t('compCarbonAdminDataAdmin.label-4')"
        centered
        v-slot="props"
      >
        {{ props.row.entity.referent.email }}
      </b-table-column>
      <b-table-column
        sortable
        :custom-sort="sortIntensity"
        :label="$t('compCarbonAdminDataAdmin.label-2')"
        numeric
        v-slot="props"
      >
        {{ props.row.carbonSynthesis.intensity | splitNumberTons }}
      </b-table-column>
      <b-table-column
        field="budget"
        sortable
        :label="$t('compCarbonAdminDataAdmin.label-11')"
        numeric
        v-slot="props"
      >
        {{ props.row.budget }}
      </b-table-column>
      <b-table-column
        field="workforce"
        sortable
        :label="$t('compCarbonAdminDataAdmin.label-5')"
        numeric
        v-slot="props"
      >
        {{ props.row.boundary.workforce }}
      </b-table-column>
      <b-table-column v-slot="props">
        <div class="panel-block">
          <b-tooltip
            :label="$t('compCarbonAdminDataAdmin.tooltip-2')"
            type="is-black"
          >
            <b-button
              size="is-small"
              icon-left="external-link"
              @click="loadGHGILink(props.row)"
            />
          </b-tooltip>
          <b-tooltip
            :label="$t('compCarbonAdminDataAdmin.tooltip-4')"
            type="is-black"
          >
            <b-button
              size="is-small"
              icon-left="files-o"
              @click="openCopy(props.row)"
            />
          </b-tooltip>
          <b-tooltip
            :label="$t('compCarbonAdminDataAdmin.tooltip-3')"
            type="is-black"
          >
            <b-button
              size="is-small"
              icon-left="trash"
              @click="deleteGHGI(props.row)"
            />
          </b-tooltip>
        </div>
      </b-table-column>
      <template #detail="props">
        <article class="media">
          <div class="media-content">
            <div class="content">
              <b-carousel
                v-model="carouselValue"
                :autoplay="false"
                indicator-style="is-lines"
                :indicator-inside="true"
              >
                <b-carousel-item>
                  <ghgi-pie
                    :height="250"
                    :enabledLabels="false"
                    :ghgi="props.row"
                    titleSize="14px"
                    :exporting="false"
                    :modules="modules"
                    :colors="colors"
                  />
                </b-carousel-item>
                <b-carousel-item>
                  <ghgi-bar
                    :height="250"
                    :enabledLabels="false"
                    :ghgi="props.row"
                    :exporting="false"
                    :modules="modules"
                    :colors="colors"
                  />
                </b-carousel-item>
              </b-carousel>
            </div>
          </div>
        </article>
      </template>
      <section slot="empty" class="section">
        <div class="content has-text-grey has-text-centered">
          <p>
            <i class="fa fa-ban" style="font-size: 64px"></i>
          </p>
          <p>{{ $t('compCarbonAdminDataAdmin.info-2') }}</p>
        </div>
      </section>
    </b-table>

    <b-modal :active.sync="displayCopy" has-modal-card @close="closeCopy">
      <div class="modal-card" style="width: 900px; height: auto">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ $t('compCarbonAdminDataAdmin.title-1') }}
          </p>
        </header>
        <form @submit.prevent="validateBeforeSubmit">
          <section class="modal-card-body">
            <b-field
              :label="$t('compCarbonAdminDataAdmin.label-17') + ' *'"
              :type="{ 'is-danger': errors.has('toGHGI') }"
              :message="errors.first('toGHGI')"
            >
              <b-input
                name="toGHGI"
                :placeholder="$t('compCarbonAdminDataAdmin.label-17') + '*'"
                v-model="toGHGI"
                v-validate="'required|numeric'"
              ></b-input>
            </b-field>
            <b-field
              :label="$t('compCarbonAdminDataAdmin.label-18') + ' *'"
              :type="{ 'is-danger': errors.has('modules') }"
              :message="errors.first('modules')"
            >
              <b-select
                name="modules"
                :placeholder="$t('compCarbonAdminDataAdmin.label-18') + ' *'"
                v-validate="'required'"
                expanded
                v-model="module2copy"
              >
                <option
                  :value="m"
                  v-for="(m, index) in coreStore.activeModules(false)"
                  :key="index"
                >
                  {{ $t(m) }}
                </option>
              </b-select>
            </b-field>
            <b-field
              :label="$t('compCarbonAdminDataAdmin.label-19')"
              :type="{ 'is-danger': errors.has('tag') }"
              :message="errors.first('tag')"
            >
              <b-input
                name="tag"
                :placeholder="$t('compCarbonAdminDataAdmin.label-19')"
                v-model="toTag"
              ></b-input>
            </b-field>
            <hr />
            <nav>
              <div class="level-left"></div>
              <div class="buttons level-right">
                <b-button class="button" @click="closeCopy" icon-left="close">
                  {{ $t('cancel') }}
                </b-button>
                <button type="submit" class="level-item button is-primary">
                  {{ $t('validate') }}
                </button>
              </div>
            </nav>
          </section>
        </form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Modules from '@/models/Modules.js'
import carbonService from '@/services/carbonService'
import { useSuperadminStore } from '@/stores/superadmin.js'
import { useCoreStore } from '@/stores/core.js'

export default {
  name: 'GES1point5',
  inject: ['$validator'],
  components: {
    'ghgi-pie': () => import('@/components/graphics/GHGIPie.vue'),
    'ghgi-bar': () => import('@/components/graphics/GHGIBar.vue'),
    'ghgis-boxplot': () => import('@/components/graphics/GHGIsBoxPlot.vue'),
    'ghgis-timelines': () => import('@/components/graphics/GHGIsTimelines.vue')
  },
  data () {
    return {
      cModule: { icon: '', text: '' },
      submittedFilter: false,
      yearFilter: [new Date().getFullYear() - 1],
      error: '',
      displayCopy: false,
      toGHGI: null,
      toTag: null,
      cghgi: null,
      module2copy: null
    }
  },
  setup () {
    const superadminStore = useSuperadminStore()
    const coreStore = useCoreStore()
    return { superadminStore, coreStore }
  },
  computed: {
    entityClass: function () {
      return this.coreStore.entityClass
    },
    modules: function () {
      return this.coreStore.activeModules(true)
    },
    activeModulesObjs: function () {
      return this.coreStore.activeModulesObjs
    },
    colors: function () {
      return this.coreStore.modulesColors
    },
    allGHGI: function () {
      return this.superadminStore.allGHGI
    },
    nStructure: function () {
      return Array.from(
        new Set(this.filteredGHGI.map((ghgi) => ghgi.entity.id))
      ).length
    },
    filteredGHGI: function () {
      return this.allGHGI.filter(
        (ghgi) =>
          this.yearFilter.includes(ghgi.year) &&
          ghgi.isSubmitted === this.submittedFilter
      )
    },
    allYears: function () {
      return this.superadminStore.allGHGIYears
    },
    /*
      // FIXME : ghgi.entity.administraiton renvoie undefined dans le cas d'une entity car administration est propre a un laboratory et pas une entity
      // FIXME : ca fait bugger la vue "Les Données" dans le menu latéral d'administration
      administrations: function () {
      let data = Array.from(new Set(this.allGHGI.map(ghgi => ghgi.entity.administrations.map(admin => admin.name)).flat())).sort()
      return data.filter((option) => {
        return (
          option
            .toString()
            .toLowerCase()
            .indexOf(this.administrationsFilter.toLowerCase()) >= 0
        )
      })
    }, */
    averageIntensity: function () {
      let allIntensities = this.filteredGHGI.map(
        (ghgi) => ghgi.carbonSynthesis.intensity / ghgi.workforce
      )
      if (this.filteredGHGI.length > 0) {
        return (
          allIntensities.reduce((sum, val) => {
            return sum + val
          }, 0) / this.filteredGHGI.length
        )
      } else {
        return 0
      }
    },
    exportLabel: function () {
      return (
        this.$t('compCarbonAdminDataAdmin.label-9') +
        ' (' +
        this.filteredGHGI.length +
        ' ' +
        this.$t('ghgi') +
        ')'
      )
    },
    options: function () {
      return this.coreStore.options
    },
    dropdownModules: function () {
      let modules = [
        {
          icon: 'flask',
          text: this.$t('compCarbonAdminDataAdmin.label-7'),
          name: 'GHGIs'
        }
      ]
      for (let module of this.coreStore.activeModules(false)) {
        modules.push({
          icon: this.getIcon(module),
          text: this.$t(module),
          name: module
        })
      }
      return modules
    }
  },
  methods: {
    upateGHGIs () {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      })
      this.superadminStore
        .getAllGHGI(this.yearFilter)
        .then((data) => {
          loadingComponent.close()
        })
        .catch(() => {
          loadingComponent.close()
        })
    },
    loadGHGILink (ghgi) {
      let route = this.$router.resolve({
        path: '/ges-1point5-results/' + ghgi.uuid
      })
      window.open(route.href, '_blank')
    },
    openCopy (ghgi) {
      this.displayCopy = true
      this.cghgi = ghgi
    },
    closeCopy () {
      this.displayCopy = false
      this.cghgi = null
      this.toGHGI = null
      this.toTag = null
      this.module2copy = null
    },
    validateBeforeSubmit () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          const loadingComponent = this.$buefy.loading.open({
            container: null
          })
          carbonService
            .copyData({
              fromGHGI: this.cghgi.id,
              toGHGI: this.toGHGI,
              toTag: this.toTag,
              module2copy: this.module2copy
            })
            .then(() => {
              this.closeCopy()
              loadingComponent.close()
            })
            .catch(() => {
              loadingComponent.close()
            })
        }
      })
    },
    deleteGHGI (ghgi) {
      let self = this
      this.$buefy.dialog.confirm({
        title: self.$t('compCarbonAdminDataAdmin.message-4'),
        message:
          self.$t('compCarbonAdminDataAdmin.message-5') +
          '<strong>' +
          ghgi.year +
          '</strong>' +
          self.$t('compCarbonAdminDataAdmin.message-6') +
          '<strong>' +
          ghgi.entity.name +
          '</strong> ?',
        confirmText: self.$t('validate'),
        cancelText: self.$t('cancel'),
        canCancel: ['escape', 'button', 'outside'],
        onConfirm: function () {
          const loadingComponent = self.$buefy.loading.open({
            container: null
          })
          self.superadminStore
            .deleteGHGI(ghgi.id)
            .then((data) => {
              loadingComponent.close()
            })
            .catch(() => {
              loadingComponent.close()
            })
        }
      })
    },
    getIcon: function (module) {
      return Modules.getIcon(module)
    },
    sortStrToNum (a, b, isAsc) {
      if (isAsc) {
        return Number(a) - Number(b)
      } else {
        return Number(b) - Number(a)
      }
    },
    sortIntensity (a, b, isAsc) {
      return this.sortStrToNum(
        a.carbonSynthesis.intensity,
        b.carbonSynthesis.intensity,
        isAsc
      )
    }
  },
  mounted () {
    this.upateGHGIs()
  }
}
</script>
